import React, { useContext, useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";

import fullLogo from "../../assets/images/svg/unicornio-brown.svg";
import kidsImg from "../../assets/images/unicornio-kids.png";
import babyImg from "../../assets/images/unicornio-baby.png";
import homemImg from "../../assets/images/unicornio-homem.png";
import mulherImg from "../../assets/images/unicornio-mulher.png";

import { useModal } from "../../context/ModalContext";
import { UserContext } from "../../context/UserContext";
import { collectionPath, commercialSolutionsPath } from "../../utils/anamneseVersion";
import { getUserPersonas } from "../../services/AnamnesisService";
import { persistExistingAnamnese } from "../../utils/existingAnamnese";
import {
  clearPersonaSessionState,
  switchActivePersona,
} from "../../utils/personaStorage";

export function formatAnamneseDate(rawDate) {
  if (!rawDate) return null;
  const str = String(rawDate).trim();
  const match = str.match(/^(\d{4})-(\d{2})-(\d{2})/);
  if (match) {
    const [, y, m, d] = match;
    return `${d}/${m}/${y}`;
  }
  try {
    const d = new Date(str.replace(" ", "T"));
    if (Number.isNaN(d.getTime())) return null;
    return d.toLocaleDateString("pt-BR");
  } catch {
    return null;
  }
}

export function getPersonaAudience(p) {
  const origin = String(p?.anamnese?.origin || p?.origin || "").toLowerCase();
  if (origin.includes("baby") || origin.includes("my-baby")) return "baby";
  if (origin.includes("kids") || origin.includes("my-kids")) return "kids";
  return "adulto";
}

export function getPersonaAvatar(p) {
  if (p?.image_url) return p.image_url;
  const audience = getPersonaAudience(p);
  const gender = String(p?.gender || "1");
  if (audience === "baby") return babyImg;
  if (audience === "kids") return kidsImg;
  return gender === "2" ? mulherImg : homemImg;
}

export function getPersonaRule(p) {
  const hasActiveSub = Boolean(
    p?.has_active_subscription ||
    ["active", "cycle_complete"].includes(
      String(p?.subscription?.status || p?.subscription?.sub_status || "").toLowerCase()
    )
  );

  // Regra 1: Já tem assinatura ativa -> vai direto para o dashboard
  if (hasActiveSub) {
    const planName =
      p?.subscription?.plan_name ||
      p?.subscription?.plan_identifier ||
      "Assinatura Ativa";
    return {
      type: "active_subscription",
      hasActiveSub: true,
      planName,
      target: "dashboard",
    };
  }

  const anm = p?.anamnese || null;
  const isV2 = anm?.version === "v2" || anm?.origin === "anamnese_v2";
  const hasV2Completed = Boolean(
    isV2 && (anm?.completed_submission_uuid || anm?.anm_id)
  );

  // Regra 2: Já tem anamnese v2 completa -> vai para a página de compra de soluções
  if (hasV2Completed) {
    return {
      type: "anamnese_v2",
      hasActiveSub: false,
      target: "solutions",
    };
  }

  // Regra 3: Não tem nenhuma v2 completa ou tem v1 -> vai para responder anamnese
  return {
    type: "pending_anamnese",
    hasActiveSub: false,
    target: "questionnaire",
  };
}

export function getPersonaOrderingRank(p) {
  // 1: Principal / Titular
  if (Number(p?.is_owner || 0) === 1) return 1;

  // 2: Assinatura ativa
  const hasActiveSub = Boolean(
    p?.has_active_subscription ||
    ["active", "cycle_complete", "awaiting_regularization", "suspended", "paused"].includes(
      String(p?.subscription?.status || p?.subscription?.sub_status || "").toLowerCase()
    )
  );
  if (hasActiveSub) return 2;

  // 3: Com anamnese
  const hasAnamnese = Boolean(
    p?.anamnese?.anm_id ||
    p?.anamnese?.completed_submission_uuid ||
    p?.anamnese?.in_progress_submission_uuid ||
    p?.anm_id ||
    p?.anm_date ||
    p?.anamnese?.date ||
    (Array.isArray(p?.anamneses) && p.anamneses.length > 0)
  );
  if (hasAnamnese) return 3;

  // 4: Sem anamnese
  return 4;
}

const ModalCardPesquisa = () => {
  const navigate = useNavigate();
  const { modalData, openCount, closeModal } = useModal();
  const { isAuthenticated, loadingAuth, usrId } = useContext(UserContext);

  const shouldCheckAnamnese = !modalData?.skipExistingCheck;

  const [view, setView] = useState(() => (shouldCheckAnamnese ? "loading" : "default"));
  const [personaItems, setPersonaItems] = useState([]);

  useEffect(() => {
    if (!shouldCheckAnamnese) {
      setView("default");
      return;
    }
    if (loadingAuth) {
      setView("loading");
      return;
    }
    if (!isAuthenticated) {
      setView("default");
      return;
    }

    let cancelled = false;
    setView("loading");
    setPersonaItems([]);

    getUserPersonas()
      .then((personas) => {
        if (cancelled) return;
        const activePersonas = (Array.isArray(personas) ? personas : [])
          .filter(
            (p) =>
              p &&
              (Number(p.status) === 1 ||
                String(p.status) === "1" ||
                typeof p.status === "undefined")
          )
          .sort((a, b) => {
            const rankA = getPersonaOrderingRank(a);
            const rankB = getPersonaOrderingRank(b);
            if (rankA !== rankB) return rankA - rankB;
            return Number(b.per_id || b.id || 0) - Number(a.per_id || a.id || 0);
          });

        if (activePersonas.length === 0) {
          setView("default");
          return;
        }

        setPersonaItems(activePersonas);
        setView("family");
      })
      .catch((err) => {
        console.error("[ModalPesquisa] Erro ao buscar personas:", err);
        if (!cancelled) setView("default");
      });

    return () => {
      cancelled = true;
    };
  }, [shouldCheckAnamnese, isAuthenticated, loadingAuth, openCount]);

  const closeModalThenNavigate = (to) => {
    const el = document.getElementById("bootstrapModal");
    const bs = window?.bootstrap;
    if (el && bs?.Modal) {
      const inst = bs.Modal.getInstance(el) || bs.Modal.getOrCreateInstance(el);
      const onHidden = () => {
        el.removeEventListener("hidden.bs.modal", onHidden);
        navigate(to);
      };
      el.addEventListener("hidden.bs.modal", onHidden);
      inst.hide();
    } else {
      navigate(to);
    }
  };

  const dismissThenGo = (to) => (e) => {
    e.preventDefault();
    try {
      switchActivePersona(null);
    } catch {}
    closeModalThenNavigate(to);
  };

  const handleSelectPersona = async (persona) => {
    const pid = persona.per_id || persona.id;
    if (!pid) return;

    const rule = getPersonaRule(persona);
    const audience = getPersonaAudience(persona);

    switchActivePersona(pid, usrId);

    // Caso 1: Assinatura Ativa -> vai direto para o dashboard
    if (rule.target === "dashboard") {
      closeModalThenNavigate("/minha-conta/dashboard");
      return;
    }

    // Caso 2: Anamnese v2 completa -> vai para a página de compra de soluções
    if (rule.target === "solutions") {
      await persistExistingAnamnese({
        personaId: pid,
        displayName: persona.name,
        anamnese: {
          id: persona.anamnese?.anm_id,
          profileUuid: persona.profile_uuid,
          submissionUuid: persona.anamnese?.completed_submission_uuid,
          v2: true,
        },
      });

      if (persona.anamnese?.anm_id) {
        try {
          localStorage.setItem("currentAnamnesis", String(persona.anamnese.anm_id));
        } catch {}
      }

      const solutionsUrl = commercialSolutionsPath(audience);
      closeModalThenNavigate(solutionsUrl);
      return;
    }

    // Caso 3: Não tem v2 completa ou tem v1 -> vai para responder anamnese
    try {
      const grupoSlug = audience === "baby" ? "my-baby" : audience === "kids" ? "my-kids" : "adulto";
      localStorage.setItem("userAnswers", JSON.stringify({ origin: grupoSlug }));
    } catch {}

    const questionnaireUrl = collectionPath(audience, { personaId: pid });
    closeModalThenNavigate(questionnaireUrl);
  };

  return (
    <div className="ModalCardPesquisa" style={{ backgroundColor: "#E6DDCE", position: "relative" }}>
      <button
        type="button"
        className="btn-close position-absolute"
        style={{ top: "18px", right: "18px", zIndex: 10 }}
        data-bs-dismiss="modal"
        aria-label="Close"
        onClick={() => closeModal?.()}
      />
      <div className="container">
        <div className="row justify-content-center">
          <div className="col-auto mb-3 mb-lg-4">
            <img src={fullLogo} alt="Puríssima" style={{ maxHeight: "85px" }} />
          </div>
        </div>

        {view === "loading" && (
          <div className="row justify-content-center align-items-center py-5">
            <div className="col-auto text-center">
              <div className="spinner-border text-secondary" role="status" />
              <p className="small text-muted mt-2">Carregando perfis...</p>
            </div>
          </div>
        )}

        {view === "family" && (
          <div className="row justify-content-center align-items-center">
            <div className="col-12 col-lg-10 text-center">
              <h3 className="my-2 my-lg-3">
                <strong>Selecione o perfil</strong>
              </h3>
              <p className="mb-3 text-muted" style={{ fontSize: "14px" }}>
                Escolha para quem deseja personalizar, escolher o plano ou acessar o painel.
              </p>

              <div className="persona-pick-list mb-3">
                {personaItems.map((persona) => {
                  const rule = getPersonaRule(persona);
                  const avatar = getPersonaAvatar(persona);
                  const isOwner = Boolean(Number(persona.is_owner) === 1);
                  const personaId = persona.per_id || persona.id;

                  const rawDate =
                    persona?.anamnese?.date ||
                    persona?.anm_date ||
                    persona?.anamnese?.created_at ||
                    (Array.isArray(persona?.anamneses) && persona.anamneses.length > 0
                      ? persona.anamneses[0]?.created_at || persona.anamneses[0]?.date
                      : null);
                  const anamneseDateFormatted = formatAnamneseDate(rawDate);

                  return (
                    <button
                      key={personaId}
                      type="button"
                      className="persona-pick-card"
                      onClick={() => handleSelectPersona(persona)}
                    >
                      <img
                        src={avatar}
                        alt={persona.name}
                        className="persona-pick-avatar"
                      />
                      <div className="persona-pick-info">
                        <div className="persona-pick-name">
                          <span>{persona.name}</span>
                          <span
                            className="text-muted ms-1"
                            style={{ fontSize: "11px", fontWeight: "normal", opacity: 0.65 }}
                          >
                            ({personaId})
                          </span>
                          {isOwner && (
                            <span
                              className="badge bg-secondary ms-2"
                              style={{ fontSize: "10px", verticalAlign: "middle" }}
                            >
                              Titular
                            </span>
                          )}
                        </div>
                        {persona.age ? (
                          <div
                            className="persona-pick-age text-muted"
                            style={{ fontSize: "13px", marginTop: "2px" }}
                          >
                            {persona.age} {Number(persona.age) === 1 ? "ano" : "anos"}
                          </div>
                        ) : null}
                        {anamneseDateFormatted ? (
                          <div
                            className="persona-pick-anamnese-date text-muted"
                            style={{ fontSize: "12px", marginTop: "2px" }}
                          >
                            Última anamnese: {anamneseDateFormatted}
                          </div>
                        ) : null}
                      </div>

                      {rule.hasActiveSub && (
                        <div className="ms-auto ps-2">
                          <span
                            className="persona-pick-badge"
                            style={{
                              backgroundColor: "rgba(46, 125, 50, 0.15)",
                              color: "#1b5e20",
                              fontWeight: "600",
                              fontSize: "12px",
                              padding: "4px 10px",
                              borderRadius: "6px",
                              whiteSpace: "nowrap",
                            }}
                          >
                            {rule.planName}
                          </span>
                        </div>
                      )}
                    </button>
                  );
                })}
              </div>

              <div className="mt-3 pt-3 border-top text-center">
                <button
                  type="button"
                  className="btn btn-link text-decoration-none"
                  style={{ color: "#446D41", fontWeight: "600", fontSize: "14px" }}
                  onClick={() => setView("default")}
                >
                  + Criar novo perfil / Outro membro da família
                </button>
              </div>
            </div>
          </div>
        )}

        {view === "default" && (
          <div className="row justify-content-center align-items-center">
            <div className="col-12 col-lg-11 text-center">
              <h3 className="my-3 my-lg-4">
                <strong>Selecione uma opção</strong>
              </h3>

              <div className="box-content">
                <ul className="row justify-content-center brandon mb-4">
                  <li className="col-6 px-1 px-lg-2">
                    <Link
                      to={collectionPath("adulto", { fresh: true })}
                      onClick={dismissThenGo(collectionPath("adulto", { fresh: true }))}
                    >
                      <svg xmlns="http://www.w3.org/2000/svg" width="36" height="39" viewBox="0 0 36 39" fill="none">
                        <path d="M25.6152 0.61106C25.3193 0.22765 24.8932 0 24.4434 0H1.55193C1.10215 0 0.68788 0.215668 0.391972 0.599078C0.0960637 0.970507 -0.0459722 1.47373 0.0132094 1.97696L0.68788 8.06359V8.18341L3.90736 37.2627C4.01389 38.2571 4.77141 39 5.6473 39H19.7562C20.1823 39 20.5847 38.8323 20.9043 38.5088C21.2239 38.1972 21.437 37.7659 21.4961 37.2866L24.9878 9.72903L25.0707 9.11797L25.9703 2.00092C26.0294 1.4977 25.8992 0.982489 25.6033 0.599078L25.6152 0.61106ZM24.7393 1.80922L23.8279 8.96221C23.6503 9.09401 23.4491 9.20184 23.2361 9.28571C23.0704 9.3576 22.9047 9.41751 22.7271 9.47742C19.697 10.4719 16.6432 8.80645 13.696 7.2129C12.9503 6.80553 12.1691 6.38618 11.4116 6.01475C9.37574 5.03226 7.5056 4.58894 5.86035 4.72074C4.27428 4.85253 2.91311 5.48756 1.78866 6.63779L1.25602 1.78525C1.24419 1.65346 1.29153 1.5576 1.32704 1.50968C1.36255 1.46175 1.43357 1.38986 1.55193 1.38986H24.4315C24.5499 1.38986 24.6328 1.46175 24.6683 1.50968C24.7038 1.5576 24.763 1.65346 24.7393 1.78525V1.80922Z"/>
                        <path d="M32.0439 10.3164C30.9638 10.3164 29.9782 10.7674 29.2627 11.4917C28.5471 12.2161 28.1016 13.2137 28.1016 14.3071V20.1838H35.9727V14.3071C35.9727 13.2411 35.5676 12.2434 34.8251 11.4917C34.0825 10.7401 33.0969 10.3301 32.0439 10.3301V10.3164ZM35.2571 14.3071C35.2571 14.3071 35.2301 14.4164 35.2031 14.4437C35.1761 14.4711 35.1221 14.4984 35.0681 14.4984C34.9601 14.4984 34.8656 14.4027 34.8656 14.2934C34.8656 13.5281 34.5685 12.8174 34.042 12.2844C33.5155 11.7514 32.7999 11.4507 32.0574 11.4507C31.9494 11.4507 31.8548 11.3551 31.8548 11.2457C31.8548 11.1364 31.9494 11.0407 32.0574 11.0407C32.9079 11.0407 33.718 11.3824 34.3255 11.9974C34.9331 12.6124 35.2706 13.4324 35.2706 14.2934L35.2571 14.3071Z" />
                        <path d="M28.1016 27.2751C28.1016 29.4754 29.8702 31.2658 32.0439 31.2658C33.0969 31.2658 34.0825 30.8558 34.8251 30.1041C35.5676 29.3524 35.9727 28.3548 35.9727 27.2888V21.3438H28.1016V27.2888V27.2751Z" />
                      </svg>
                      <strong>ADULTO</strong>
                      <small>≥ 13 ANOS</small>
                    </Link>
                  </li>
                  <li className="col-6 px-1 px-lg-2">
                    <Link
                      to={collectionPath("kids", { fresh: true })}
                      onClick={dismissThenGo(collectionPath("kids", { fresh: true }))}
                    >
                      <svg xmlns="http://www.w3.org/2000/svg" width="26" height="38" viewBox="0 0 26 38" fill="none">
                        <path d="M24.8519 0.595392C24.5673 0.221813 24.1574 0 23.7248 0H1.70751C1.27491 0 0.876456 0.210138 0.591847 0.583717C0.307239 0.945622 0.170627 1.43594 0.227549 1.92627L0.876456 7.85684V7.97358L3.973 36.3072C4.07545 37.2762 4.80405 38 5.64649 38H19.2166C19.6265 38 20.0135 37.8366 20.3209 37.5214C20.6283 37.2178 20.8332 36.7975 20.8901 36.3306L24.2485 9.47957L24.3282 8.88418L25.1934 1.94962C25.2503 1.45929 25.1251 0.957296 24.8405 0.583717L24.8519 0.595392ZM24.0094 1.76283L23.1328 8.73241C22.9621 8.86083 22.7685 8.9659 22.5636 9.04762C22.4042 9.11767 22.2449 9.17604 22.0741 9.23441C19.1597 10.2034 16.2225 8.58064 13.3878 7.02796C12.6706 6.63103 11.9193 6.22243 11.1907 5.86052C9.23256 4.90323 7.43383 4.47127 5.85141 4.59969C4.32591 4.72811 3.01671 5.34685 1.9352 6.46759L1.4229 1.73948C1.41152 1.61106 1.45706 1.51767 1.49121 1.47097C1.52536 1.42427 1.59367 1.35422 1.70751 1.35422H23.7134C23.8273 1.35422 23.907 1.42427 23.9411 1.47097C23.9753 1.51767 24.0322 1.61106 24.0094 1.73948V1.76283Z" />
                      </svg>
                      <strong>CRIANÇA</strong>
                      <small>DE 3 A 12 ANOS</small>
                    </Link>
                  </li>
                </ul>

                {personaItems.length > 0 && (
                  <div className="text-center">
                    <button
                      type="button"
                      className="btn btn-link text-muted text-decoration-none"
                      style={{ fontSize: "14px" }}
                      onClick={() => setView("family")}
                    >
                      ← Voltar aos perfis da família
                    </button>
                  </div>
                )}
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default ModalCardPesquisa;
